/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * Admin API boundary. Zod schemas mirror the backend `AdminUserPage` /
 * `AdminUserView` records exactly; responses are `.parse()`d so contract drift
 * throws at the boundary rather than surfacing as a downstream type error.
 */

import { z } from 'zod';
import { tdFetch } from './tdfetch.js';

export const AdminUserViewSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string().nullable(),
  salutation: z.string().nullable(),
  groups: z.array(z.string()),
  authenticationTypes: z.array(z.string()),
  custodianCount: z.number().int(),
});
export type AdminUserView = z.infer<typeof AdminUserViewSchema>;

export const AdminUserPageSchema = z.object({
  users: z.array(AdminUserViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  skip: z.number().int(),
});
export type AdminUserPage = z.infer<typeof AdminUserPageSchema>;

/** Thrown when the server rejects the caller as non-admin (403). */
export class AdminForbiddenError extends Error {
  constructor() {
    super('Admin access forbidden');
    this.name = 'AdminForbiddenError';
  }
}

/** Thrown when the session is unusable after tdFetch's refresh attempt (401). */
export class AdminUnauthorizedError extends Error {
  constructor() {
    super('Admin session expired');
    this.name = 'AdminUnauthorizedError';
  }
}

/**
 * Fetch one page of the admin customer list. `tdFetch` has already attempted a
 * transparent token refresh on 401; a surviving 401 means the session is dead
 * (→ login), a 403 means valid-but-not-admin (→ BaNotAuthorized), and any other
 * non-2xx or a Zod mismatch throws for the caller's retry callout.
 *
 * @param limit page size
 * @param skip offset
 * @returns the validated page
 */
export const fetchUserPage = async (
  limit: number,
  skip: number
): Promise<AdminUserPage> => {
  const res = await tdFetch(`/api/admin/users?limit=${limit}&skip=${skip}`);
  if (res.status === 401) {
    throw new AdminUnauthorizedError();
  }
  if (res.status === 403) {
    throw new AdminForbiddenError();
  }
  if (!res.ok) {
    throw new Error(`Failed to load users: ${res.status} ${res.statusText}`);
  }
  return AdminUserPageSchema.parse(await res.json());
};
